import { Clock, Users, CalendarClock, Award, Target, ClipboardCheck, Settings, BookOpen, Wallet, Accessibility } from 'lucide-react';

/**
 * QualiopiInfoSection — Mandatory Qualiopi fields rendered as a structured,
 * two-column information block on the public course detail page.
 * The component degrades gracefully: if a field is missing it is skipped.
 */
export const QualiopiInfoSection = ({ info }) => {
  if (!info || typeof info !== 'object') return null;

  const items = [
    { key: 'duree', icon: Clock, title: 'Durée de la formation', body: info.duree, kind: 'text' },
    { key: 'tarif', icon: Wallet, title: 'Tarif', body: info.tarif, kind: 'text' },
    { key: 'participants', icon: Users, title: 'Nombre de participants', body: info.participants, kind: 'text' },
    { key: 'delai_acces', icon: CalendarClock, title: "Délai d'accès", body: info.delai_acces, kind: 'text' },
    { key: 'prerequis', icon: Award, title: 'Prérequis', body: info.prerequis, kind: 'text' },
    { key: 'public', icon: Accessibility, title: 'Public concerné', body: info.public, kind: 'text' },
    { key: 'objectifs', icon: Target, title: 'Objectifs pédagogiques', body: info.objectifs, kind: 'list' },
    { key: 'evaluation', icon: ClipboardCheck, title: "Moyens d'évaluation des acquis", body: info.evaluation, kind: 'list' },
    { key: 'modalites', icon: Settings, title: "Modalités d'exécution", body: info.modalites, kind: 'text' },
    { key: 'moyens_techniques', icon: Settings, title: 'Moyens techniques', body: info.moyens_techniques, kind: 'list' },
    { key: 'moyens_pedagogiques', icon: BookOpen, title: 'Moyens pédagogiques', body: info.moyens_pedagogiques, kind: 'list' },
  ].filter((it) => {
    if (it.kind === 'list') return Array.isArray(it.body) && it.body.length > 0;
    return typeof it.body === 'string' && it.body.trim().length > 0;
  });

  if (items.length === 0) return null;

  return (
    <section
      className="py-12 md:py-20 bg-[#FAF7F0] border-t border-[#1F2A4A]/10"
      data-testid="qualiopi-info-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 mb-10 md:mb-14 items-end">
          <div className="col-span-12 md:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-3 font-semibold">
              Fiche officielle · Qualiopi
            </p>
            <h2 className="font-light text-2xl md:text-4xl leading-tight text-[#1F2A4A]">
              Informations <span className="font-serif italic">pédagogiques & légales.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-sm text-[#4A5568] font-light leading-relaxed">
              Toutes les informations réglementaires de cette formation, conformes
              au Référentiel National Qualité (Qualiopi) — accessibles publiquement
              et tenues à jour.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 lg:gap-x-16 gap-y-8">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <div
                key={it.key}
                className="border-t border-[#1F2A4A]/12 pt-5"
                data-testid={`qualiopi-${it.key}`}
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <Icon className="w-4 h-4 text-[#C9B896]" strokeWidth={2} />
                  <h3 className="text-xs uppercase tracking-[0.2em] text-[#1F2A4A] font-semibold">
                    {it.title}
                  </h3>
                </div>
                {it.kind === 'list' ? (
                  <ul className="space-y-2">
                    {it.body.map((line, i) => (
                      <li
                        key={`${it.key}-${i}`}
                        className="flex items-start gap-2.5 text-[15px] text-[#1F2A4A]/90 font-light leading-relaxed"
                      >
                        <span className="text-[#C9B896] mt-1.5 shrink-0">▸</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[15px] text-[#1F2A4A]/90 font-light leading-relaxed whitespace-pre-line">
                    {it.body}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
